import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';

import { TProjectItem } from '@core/interfaces/TProjectItem';
import { AllowedRoleDirective } from '@shared/directives/allowed-role.directive';
import { HeaderComponent } from '@theme/header/header.component';

import { ProjectsContentComponent } from '../components/gingr-content/projects-content/projects-content.component';
import { NewProjectModalComponent } from '../components/gingr-new-items-modals/new-project-modal/new-project-modal.component';
import {
  ContentShellComponent,
  EmptyStateComponent,
  ListItemComponent,
  PanelHeaderComponent,
} from '@shared/components/content-panel';
import { ProjectsService } from '@shared/services/projects.service';
import { ConfirmModalComponent } from 'app/features/layout/components/organisms/confirmation-modal/confirm-modal.component';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
  imports: [
    ContentShellComponent,
    EmptyStateComponent,
    HeaderComponent,
    PanelHeaderComponent,
    ListItemComponent,
    ProjectsContentComponent,
    MatIcon,
    MatIconButton,
    MatMenuModule,
    MatTooltip,
    AllowedRoleDirective,
  ],
})
export class ProjectsComponent implements OnInit {
  private static readonly DESCRIPTION_TOOLTIP_MAX = 400;

  projects: TProjectItem[] = [];
  selectedProject: TProjectItem | null = null;

  get sortedProjects(): TProjectItem[] {
    return this.projects.slice().sort((a, b) => a.title.localeCompare(b.title));
  }

  private readonly dialog = inject(MatDialog);
  private readonly projectsService = inject(ProjectsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.projectsService
      .getProjectsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.projects = results;
        this.syncSelectionFromRoute();
      });
    this.projectsService.setProjects();

    this.route.firstChild?.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncSelectionFromRoute());
  }

  selectProject(project: TProjectItem): void {
    this.selectedProject = project;
    this.router.navigateByUrl(`/projects/${project._id}`);
  }

  /** Trimmed description for tooltip / empty checks. */
  protected projectDescription(project: TProjectItem): string {
    return project.description?.trim() ?? '';
  }

  /** Accessible summary (capped); list tooltip may truncate further. */
  protected projectDescriptionAria(project: TProjectItem): string {
    const text = this.projectDescription(project);
    if (!text) {
      return 'No project description';
    }
    const max = 500;
    return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
  }

  /** Tooltip text: truncated body or a clear empty state. */
  protected projectDescriptionTooltip(project: TProjectItem): string {
    const text = this.projectDescription(project);
    if (!text) {
      return 'No project description';
    }
    const max = ProjectsComponent.DESCRIPTION_TOOLTIP_MAX;
    if (text.length <= max) {
      return text;
    }
    return `${text.slice(0, max - 1)}…`;
  }

  addNewItem(): void {
    this.dialog.open(NewProjectModalComponent);
  }

  onDeleteProject(project: TProjectItem, event: MouseEvent): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Delete Project',
        text: `Are you sure you want to delete the project "<strong>${project.title}</strong>"? This action cannot be undone.`,
      },
    });

    dialogRef.componentInstance.actionCallback
      .pipe(
        switchMap(() => this.projectsService.deleteProject(project._id as string)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.selectedProject?._id === project._id) {
          this.selectedProject = null;
          this.router.navigateByUrl('/projects');
        }
      });
  }

  private syncSelectionFromRoute(): void {
    const childRoute = this.route.firstChild;
    if (!childRoute) return;

    const id = childRoute.snapshot.params.id;
    if (id && this.projects.length) {
      const match = this.projects.find(p => p._id === id);
      if (match) {
        this.selectedProject = match;
      }
    }
  }
}
